import React from 'react';
import { Link } from 'react-router-dom';
import {
  Star, Award, Home, Heart, Camera, GraduationCap, Users, Briefcase,
  Scissors, Sparkles, Smile, PawPrint, ArrowRight, Phone, HeadphonesIcon, Clock, Gift } from
'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, services, whyChooseUs, reviews } from '../mock';
import SEO from '../components/SEO';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Dog Grooming Manchester | Luxury Salon & Academy in Denton"
        description="Award-winning dog grooming salon and CPD accredited training academy in Manchester. Professional grooming for all breeds in Denton, Greater Manchester. UK Top 10 Best New Business 2025."
        keywords="dog grooming Manchester, dog grooming Denton, Manchester dog groomers, pet spa Manchester, dog grooming near me Manchester, puppy grooming Manchester, award winning dog groomers Manchester, dog grooming academy Manchester"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sage-50 via-white to-sage-100 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-sage-200 rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-sage-200 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 py-20 lg:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <div className="inline-block bg-sage-100 text-sage-700 px-4 py-2 rounded-full text-sm font-semibold">
                ⭐ {businessInfo.rating.stars} Rating • {businessInfo.rating.count}+ Happy Customers
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-sage-900 leading-tight">
                Luxury Dog Grooming & Training Academy
              </h1>
              <p className="text-xl text-sage-700 leading-relaxed">
                Professional dog pampering and expert training for future dog stylists.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 px-8 text-white font-medium shadow-lg">Contact Us</Button>
                </Link>
                <Link to="/academy">
                  <Button size="lg" className="bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 px-8 text-white font-medium shadow-lg">
                    Enquire About Courses
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-sage-200 to-sage-200 rounded-3xl blur-2xl opacity-30"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_9f3fada2-34ee-435a-93bd-c381f4301c73/artifacts/11dz6ra7_Photo%2022-08-2025%2C%2009%2008%2028.jpg"
                alt="Pups & Dreams Spa & Academy storefront"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover md:object-top" />

            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section style={{paddingTop: '80px', paddingBottom: '0px'}} className="bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block bg-sage-200 text-sage-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  Our Mission
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-6">
                  Creating a Luxurious, Stress-Free Pamper Experience
                </h2>
                <p className="text-lg text-sage-700 leading-relaxed mb-6">
                  Our mission is simple: to create a luxurious, stress-free pamper experience where 
                  every dog feels safe, relaxed and loved.
                </p>
                <p className="text-lg text-sage-700 leading-relaxed">
                  Our purpose-built salon blends spa luxury with the warmth of a homely atmosphere. 
                  From soft lighting, gentle handling techniques and deluxe spa treatments, every element 
                  of our spa is designed to prioritise your dog's comfort and well-being.
                </p>
              </div>
              <div className="relative">
                <img
                  src="emilymegan1.jpeg"
                  alt="Luxury spa reception with ambient lighting"
                  className="rounded-3xl shadow-2xl w-full object-contain" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academy Teaser */}
      <section style={{paddingTop: '80px', paddingBottom: '0px'}} className="bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 mb-0 lg:mb-10">
              <img
                src="https://customer-assets.emergentagent.com/job_9f3fada2-34ee-435a-93bd-c381f4301c73/artifacts/xsp128o0_Photo%2022-08-2025%2C%2009%2022%2050.jpg"
                alt="Dog grooming training with real dogs at Pups & Dreams Academy"
                className="rounded-3xl shadow-2xl w-full h-[450px] object-cover" />
              <div className="flex lg:hidden justify-center" style={{marginTop: '60px'}}>
                <Link to="/academy">
                  <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium px-8 rounded-md shadow-lg text-white">Learn More About Our Academy</Button>
                </Link>
              </div>
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-block bg-sage-200 text-sage-800 px-4 py-2 rounded-full text-sm font-semibold">
                CPD Accredited Training
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900">
                Learn to Be a Dog Groomer
              </h2>
              <p className="text-lg text-sage-700 leading-relaxed">
                Start your dream career in dog grooming with our CPD Accredited 5-day intensive course. 
                No experience needed – just a passion for dogs!
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { icon: Users,           main: 'Small class sizes',            sub: 'Max 4 students' },
                  { icon: Home,            main: 'Real salon environment',        sub: 'Hands-on training' },
                  { icon: Scissors,        main: 'Hands-on practical experience', sub: 'Groom real dogs' },
                  { icon: Gift,       main: 'Artero starter kit worth £800', sub: 'Yours to keep!' },
                  { icon: Phone,           main: 'Business guidance',             sub: 'Ongoing support' },
                ].map(({ icon: Icon, main, sub }, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 bg-sage-50 rounded-lg">
                    <Icon className="text-sage-600 flex-shrink-0" size={22} />
                    <div>
                      <div className="font-semibold text-sage-900 text-sm">{main}</div>
                      <div className="text-xs text-sage-600">{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden lg:flex justify-start" style={{marginTop: '40px'}}>
                <Link to="/academy">
                  <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium px-8 rounded-md shadow-lg text-white">Learn More About Our Academy</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white" style={{paddingTop: '80px', paddingBottom: '80px'}}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" style={{marginTop: '20px'}}>
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              Why Choose Pups & Dreams?
            </h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              We're more than just a grooming salon – we're a family that loves your furry friends
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item) => {
              const IconComponent = {
                star: Star,
                award: Award,
                home: Home,
                heart: Heart,
                camera: Camera,
                'graduation-cap': GraduationCap
              }[item.icon];

              return (
                <Card key={item.id} className="border-sage-200 hover:shadow-lg transition-shadow bg-gradient-to-br from-white to-sage-50">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-sage-100 to-sage-200 rounded-2xl flex items-center justify-center mb-4">
                      <IconComponent className="text-sage-700" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-sage-900 mb-2">{item.title}</h3>
                    <p className="text-sage-600">{item.description}</p>
                  </CardContent>
                </Card>);
            })}
          </div>

          {/* Award Highlight */}
          <div className="max-w-4xl mx-auto mt-16 text-center bg-gradient-to-br from-amber-50 to-sage-50 rounded-3xl p-8 shadow-lg border-2 border-amber-200">
            <div className="inline-block bg-white rounded-2xl p-6 shadow-xl mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_pups-dreams-spa/artifacts/szws92dp_3512DC54-7377-4DFD-B980-96674B56E5D4.jpeg"
                alt="UK Small Business Awards 2025 - Best New Business - UK TOP 10 Winner"
                className="w-full max-w-md mx-auto rounded-xl" />
            </div>
            <h3 className="text-xl font-bold text-sage-900 mb-2">
              Award-Winning Excellence
            </h3>
            <p className="font-semibold text-sm sm:text-base mb-2 text-sage-700">
              UK TOP 10 WINNER — Best New Business
            </p>
            <p className="text-sm sm:text-base text-sage-600">UK Small Business Awards 2025</p>
          </div>

          {/* Press Feature */}
          <div className="max-w-4xl mx-auto mt-12 bg-white rounded-3xl p-8 shadow-lg border border-sage-200">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-sage-500 text-sm font-medium uppercase tracking-wider">Featured In</span>
            </div>
            <h3 className="text-2xl font-bold text-sage-900 mb-4 text-center">
              "New Denton dog grooming salon makes national awards shortlist"
            </h3>
            <p className="text-sage-700 text-center leading-relaxed mb-6 max-w-3xl mx-auto">
              "A Denton dog grooming salon that only opened its doors in May is already in the running for a prestigious national award. Pups &amp; Dreams Spa &amp; Academy has been shortlisted in the Best New Business category at the UK Small Business Awards 2025...
              <br /><br />
              The salon has racked up over 100 five-star reviews on Google in just four months."
            </p>
            <p className="text-sage-500 text-sm text-center mb-6 italic">
              — Tameside Correspondent, October 2025
            </p>
            <div className="text-center">
              <a 
                href="https://www.tamesidecorrespondent.co.uk/2025/10/15/new-denton-dog-grooming-salon-makes-national-awards-shortlist/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="border-2 border-sage-600 text-sage-700 hover:bg-sage-50">
                  Read the Full Article <ArrowRight className="ml-2" size={18} />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Salon Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-3">
                Our Salon
              </h2>
              <p className="text-lg text-sage-600">
                Take a look inside our professional grooming space
              </p>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory items-center">
              {[
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/17vko9qi_8e2af59a-769c-4a40-a557-5affe1fb41ce.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/lrlqcqkz_Photo%2022-08-2025%2C%2009%2011%2028.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/ezs4li0r_eed55b9d-19ad-4c17-915f-dfd7eecaa9ce.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/m5t6c1qm_46ce6577-f752-4296-abe7-172a664cb569.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/kyxwsnir_15cd5b17-29c3-42d1-9bfb-8afd66a2d48d.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/sw22uqd6_IMG_6113.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/atn6jalh_IMG_6114.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/cdvu0vbj_IMG_6115.jpeg',
                'https://customer-assets.emergentagent.com/job_8e5aba18-0f4c-4ba8-8a4e-aabd33fafd89/artifacts/gtei8sqg_IMG_6116.jpeg',
              ].map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`Our salon ${index + 1}`}
                  className="flex-shrink-0 h-80 w-auto rounded-xl shadow-lg snap-center"
                />
              ))}
            </div>
            <p className="text-center text-sage-500 text-sm mt-2">← Scroll to see more →</p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              Our Grooming Services
            </h2>
            <p className="text-lg text-sage-600 max-w-2xl mx-auto">
              From puppy's first groom to full spa treatments, we've got your dog covered
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {services.slice(0, 6).map((service) => {
              const IconComponent = {
                scissors: Scissors,
                heart: Heart,
                droplet: Sparkles,
                sparkles: Sparkles,
                smile: Smile,
                'paw-print': PawPrint
              }[service.icon];

              return (
                <Card key={service.id} className="border-sage-200 hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-sage-500 to-sage-600 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-sage-900 mb-2">{service.name}</h3>
                    <p className="text-sage-600 text-sm mb-3">{service.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-sage-600 text-sage-700 hover:bg-white">
                View All Services <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center gap-2 text-amber-500 mb-4">
              {[...Array(5)].map((_, i) =>
              <Star key={i} className="fill-current" size={24} />
              )}
              <span className="ml-2 text-2xl font-bold text-sage-900">{businessInfo.rating.stars}</span>
              <span className="text-sage-600">/ 5.0</span>
            </div>
            <p className="text-lg text-sage-600">Based on {businessInfo.rating.count}+ reviews</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.slice(0, 6).map((review) =>
            <Card key={review.id} className="border-sage-200 bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) =>
                  <Star key={i} className="fill-amber-400 text-amber-400" size={16} />
                  )}
                  </div>
                  <p className="text-sage-700 mb-4 italic">"{review.text}"</p>
                  <div className="flex items-center justify-between pt-3 border-t border-sage-100">
                    <span className="font-semibold text-sage-900">{review.author}</span>
                    <span className="text-sm text-sage-500">{review.date}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/reviews">
              <Button size="lg" variant="outline" className="border-2 border-sage-600 text-sage-700 hover:bg-white">
                Read All Reviews <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
            Ready to Pamper Your Pup?
          </h2>
          <p className="max-w-2xl text-xl text-sage-700 mb-8 mx-auto">
            Book your appointment today and give your dog the spa treatment they deserve
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium px-8 rounded-md shadow-lg text-white">Contact Us</Button>
            </Link>
            <a href={`tel:${businessInfo.phone}`}>
              <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-sm font-medium gap-2 px-8 rounded-md shadow-lg text-white">
                <Phone className="mr-2" size={20} />
                Call {businessInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default HomePage;